import {
  Button,
  DialogActionTrigger,
  ButtonGroup,
  DialogTitle,
  Input,
  Box,
  Icon,
  Text,
  Grid,
  VStack,
  Flex,
} from "@chakra-ui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Controller, useForm } from "react-hook-form";
import type { SubmitHandler } from "react-hook-form";
import { useState } from "react";
import { FaPlus } from "react-icons/fa";
import {
  FiFileText,
  FiHash,
  FiCalendar,
  FiList,
  FiUpload,
  FiPlus,
  FiTrash,
} from "react-icons/fi";
import { TableFieldsService } from "../../client";
import type {
  ApiError,
  TableFieldCreate,
  FieldType,
  AllowedExt,
} from "../../client";
import useCustomToast from "../../hooks/useCustomToast";
import { handleError } from "../../utils";
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTrigger,
} from "../ui/dialog";
import { Field } from "../ui/field";
import { Checkbox } from "../ui/checkbox";
import { Select } from "../ui/select";

interface AddTableFieldProps {
  tableId: string;
}

const AddTableField = ({ tableId }: AddTableFieldProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const queryClient = useQueryClient();
  const { showSuccessToast } = useCustomToast();

  // 현재 선택된 필드 타입, 숫자/문자열 타입 저장용 상태값
  const [selectedFieldType, setSelectedFieldType] = useState("");
  const [selectedNumberType, setSelectedNumberType] = useState("");
  const [_selectedStringType, setSelectedStringType] = useState("");
  const [_selectedDatetimeType, setSelectedDatetimeType] = useState("");
  const [selectionElements, setSelectionElements] = useState([
    { label: "", color: "#000000" },
  ]);

  const {
    control,
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isValid, isSubmitting },
  } = useForm<TableFieldCreate>({
    mode: "onBlur",
    criteriaMode: "all",
    defaultValues: {
      field_name: "",
      description: "",
      field_code: "",
      field_type: "string",
      is_required: true,
      string_metadata: {
        string_type: "shortText",
      },
      number_metadata: {
        number_type: "natural",
        unit: "",
        min_value: 0,
        max_value: 100,
        decimal_round: undefined,
      },
      datetime_metadata: {
        datetime_type: "datetime",
      },
      selection_metadata: { options: [] },
    },
  });

  const addSelectionElement = () => {
    setSelectionElements((prev) => [...prev, { label: "", color: "#000000" }]);
  };

  const removeSelectionElement = (index: number) => {
    setSelectionElements((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSelectionChange = (
    index: number,
    key: "label" | "color",
    value: string
  ) => {
    setSelectionElements((prev) => {
      const updated = [...prev];
      updated[index][key] = value;
      return updated;
    });
  };

  //셀렉션 중복체크 확인
  // const hasDuplicateLabels = selectionElements.some(
  //   (el, idx) => selectionElements.findIndex(e => e.label === el.label) !== idx
  // )

  const mutation = useMutation({
    mutationFn: (data: TableFieldCreate) =>
      TableFieldsService.createTableField({ requestBody: data, tableId }),
    onSuccess: () => {
      showSuccessToast("테이블 필드가 성공적으로 생성되었습니다.");
      reset();
      setIsOpen(false);
      setHasSubmitted(false);
    },
    onError: (err: ApiError) => {
      handleError(err);
      setHasSubmitted(false);
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === "tableFields",
      });
    },
  });

  const onSubmit: SubmitHandler<TableFieldCreate> = (data) => {
    if (hasSubmitted) return;
    setHasSubmitted(true);

    // 선택된 필드 타입에 따라 해당 메타데이터만 포함시키고 나머지는 null로 처리
    const filteredData: TableFieldCreate = {
      ...data,
      number_metadata:
        data.field_type === "number" ? data.number_metadata : null,
      string_metadata:
        data.field_type === "string" ? data.string_metadata : null,
      datetime_metadata:
        data.field_type === "datetime" ? data.datetime_metadata : null,
      selection_metadata:
        data.field_type === "selection" ? { options: selectionElements } : null,
      file_metadata: data.field_type === "file" ? data.file_metadata : null,
    };

    // 필드 생성 API 호출
    mutation.mutate(filteredData);
  };

  const fieldTypes = [
    { type: "string", icon: FiFileText, label: "문자열" },
    { type: "number", icon: FiHash, label: "숫자" },
    { type: "datetime", icon: FiCalendar, label: "날짜/시간" },
    { type: "selection", icon: FiList, label: "선택" },
    { type: "file", icon: FiUpload, label: "파일" },
  ];

  const AllowedExtList: AllowedExt[] = ["pdf", "ppt", "jpg", "xlsx"];

  return (
    <DialogRoot
      size={{ base: "xs", md: "lg" }}
      placement="center"
      open={isOpen}
      onOpenChange={({ open }) => setIsOpen(open)}
    >
      <DialogTrigger asChild>
        <Button value="add-item" my={4} disabled={!tableId}>
          <FaPlus fontSize="16px" />
          테이블 필드 추가
        </Button>
      </DialogTrigger>
      <DialogContent asChild>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogHeader>
            <DialogTitle>필드 추가</DialogTitle>
          </DialogHeader>

          <DialogBody>
            <VStack gap={4} align="stretch">
              <Field
                required
                invalid={!!errors.field_name}
                errorText={errors.field_name?.message}
                label="필드명"
              >
                <Input
                  id="field_name"
                  {...register("field_name", {
                    required: "필드명을 입력하세요.",
                  })}
                  placeholder="필드명을 입력하세요."
                  type="text"
                />
              </Field>

              <Field
                required
                invalid={!!errors.description}
                errorText={errors.description?.message}
                label="설명"
              >
                <Input
                  id="description"
                  {...register("description", {
                    required: "설명을 입력하세요.",
                  })}
                  placeholder="설명을 입력하세요."
                  type="text"
                />
              </Field>

              <Field
                invalid={!!errors.field_code}
                errorText={errors.field_code?.message}
                label="코드명"
              >
                <Input
                  id="field_code"
                  {...register("field_code")}
                  placeholder="CSV/XLSX 파일의 필드명을 입력하세요 (예: name)"
                  type="text"
                />
              </Field>

              <Field required label="필드 타입">
                <Grid templateColumns="repeat(4, 1fr)" gap={4}>
                  {fieldTypes.map((fieldType) => (
                    <Box
                      key={fieldType.type}
                      onClick={() => {
                        const type = fieldType.type as FieldType;

                        // 선택된 필드 타입 저장
                        setSelectedFieldType(type);
                        // React Hook Form에 선택된 타입 설정
                        setValue("field_type", type);

                        // 숫자 필드가 아닐 경우 number_metadata 초기화
                        if (type !== "number") {
                          setValue("number_metadata", null);
                        } else {
                          // 숫자 필드일 경우 기본값 설정
                          setValue("number_metadata", {
                            number_type: "natural",
                            unit: "",
                            min_value: undefined,
                            max_value: undefined,
                            decimal_round: undefined,
                          });
                        }

                        // 문자열 필드가 아닐 경우 string_metadata 초기화
                        if (type !== "string") {
                          setValue("string_metadata", null);
                        } else {
                          // 문자열 필드일 경우 기본값 설정
                          setValue("string_metadata", {
                            string_type: "shortText",
                          });
                        }

                        // 시간/날짜 필드가 아닐 경우 datime_metadata 초기화
                        if (type !== "datetime") {
                          setValue("datetime_metadata", null);
                        } else {
                          // 시간/날짜 필드일 경우 기본값 설정
                          setValue("datetime_metadata", {
                            datetime_type: "datetime",
                          });
                        }
                      }}
                      p={3}
                      borderWidth={2}
                      borderColor={
                        selectedFieldType === fieldType.type
                          ? "blue.500"
                          : "gray.200"
                      }
                      borderRadius="md"
                      cursor="pointer"
                      textAlign="center"
                      _hover={{ borderColor: "blue.300" }}
                    >
                      <Icon as={fieldType.icon} w={5} h={5} />
                      <Text mt={2} fontSize="sm">
                        {fieldType.label}
                      </Text>
                    </Box>
                  ))}
                </Grid>
              </Field>

              {selectedFieldType === "number" && (
                <>
                  <Field required label="숫자 타입">
                    <Controller
                      name="number_metadata.number_type"
                      control={control}
                      render={({ field }) => (
                        <Select
                          name="numberType"
                          placeholder="숫자 유형 선택"
                          value={field.value}
                          onChange={(val) => {
                            setSelectedNumberType(val);
                            field.onChange(val);
                          }}
                          options={[
                            { label: "자연수", value: "natural" },
                            { label: "정수", value: "integer" },
                            { label: "소수", value: "decimal" },
                          ]}
                          isRequired
                          portalled={false}
                        />
                      )}
                    />
                  </Field>

                  <Field required label="단위">
                    <Input
                      id="unit"
                      {...register("number_metadata.unit")}
                      placeholder="단위를 입력하세요 (예. kg, cm)"
                    />
                  </Field>

                  <Field required label="최소값">
                    <Input
                      id="min_value"
                      {...register("number_metadata.min_value", {
                        valueAsNumber: true,
                      })}
                      placeholder="최소값을 입력하세요"
                    />
                  </Field>

                  <Field required label="최대값">
                    <Input
                      id="max_value"
                      {...register("number_metadata.max_value", {
                        valueAsNumber: true,
                      })}
                      placeholder="최대값을 입력하세요"
                    />
                  </Field>

                  {selectedNumberType === "decimal" && (
                    <Field label="반올림 소수점">
                      <Input
                        id="decimal_round"
                        {...register("number_metadata.decimal_round", {
                          valueAsNumber: true,
                        })}
                        placeholder="반올림 소수점을 입력하세요"
                      />
                    </Field>
                  )}
                </>
              )}

              {selectedFieldType === "string" && (
                <Field required label="문자 유형">
                  <Controller
                    name="string_metadata.string_type"
                    control={control}
                    render={({ field }) => (
                      <Select
                        name="stringType"
                        placeholder="문자열 유형 선택"
                        value={field.value}
                        onChange={(val) => {
                          // 선택된 문자열 타입 저장
                          setSelectedStringType(val);
                          field.onChange(val);
                        }}
                        options={[
                          { label: "짧은 텍스트", value: "shortText" },
                          { label: "긴 텍스트", value: "longText" },
                          { label: "이메일", value: "email" },
                          { label: "전화번호", value: "phone" },
                          { label: "주소", value: "address" },
                        ]}
                        isRequired
                        portalled={false}
                      />
                    )}
                  />
                </Field>
              )}

              {selectedFieldType === "datetime" && (
                <Field required label="날짜/시간 유형">
                  <Controller
                    name="datetime_metadata.datetime_type"
                    control={control}
                    render={({ field }) => (
                      <Select
                        name="datetimeType"
                        placeholder="날짜/시간 유형 선택"
                        value={field.value}
                        onChange={(val) => {
                          // 선택된 문자열 타입 저장
                          setSelectedDatetimeType(val);
                          field.onChange(val);
                        }}
                        options={[
                          { label: "날짜/시간", value: "datetime" },
                          { label: "날짜", value: "date" },
                          { label: "시간", value: "time" },
                        ]}
                        isRequired
                        portalled={false}
                      />
                    )}
                  />
                </Field>
              )}

              {selectedFieldType === "selection" && (
                <Box>
                  <Text fontWeight="bold" mb={2}>
                    선택 요소
                  </Text>
                  {selectionElements.map((element, index) => (
                    <Flex
                      key={`selection-${index}-${element.label}`}
                      mb={2}
                      alignItems="center"
                      gap={3}
                    >
                      <Input
                        value={element.label}
                        placeholder={`라벨 ${index + 1}`}
                        onChange={(e) =>
                          handleSelectionChange(index, "label", e.target.value)
                        }
                        borderColor={
                          selectionElements.findIndex(
                            (el, i) => el.label === element.label && i !== index
                          ) !== -1
                            ? "red.500"
                            : undefined
                        }
                      />
                      <Input
                        type="color"
                        value={element.color}
                        onChange={(e) =>
                          handleSelectionChange(index, "color", e.target.value)
                        }
                        title="Pick a color"
                        width="50px"
                        p={0}
                      />
                      <Icon
                        as={FiTrash}
                        color="red.500"
                        cursor="pointer"
                        onClick={() => removeSelectionElement(index)}
                      />
                    </Flex>
                  ))}
                  <Button onClick={addSelectionElement} mt={2}>
                    <Icon as={FiPlus} mr={2} />
                    요소 추가
                  </Button>
                </Box>
              )}

              {selectedFieldType === "file" && (
                <>
                  <Field required label="파일 확장자">
                    <Controller
                      name="file_metadata.allowed_extension"
                      control={control}
                      render={({ field }) => (
                        <Select
                          name="allowedExtension"
                          placeholder="허용 확장자 선택"
                          value={field.value}
                          onChange={field.onChange}
                          options={AllowedExtList.map((ext) => ({
                            label: ext.toUpperCase(),
                            value: ext,
                          }))}
                          isRequired
                          portalled={false}
                        />
                      )}
                    />
                  </Field>
                  <Field required label="파일당 최대 크기(MB)">
                    <Input
                      type="number"
                      min={1}
                      {...register("file_metadata.max_file_size_mb", {
                        valueAsNumber: true,
                      })}
                      placeholder="예: 10"
                    />
                  </Field>
                </>
              )}

              <Controller
                name="is_required"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    id="is_required"
                    checked={!!field.value}
                    onCheckedChange={(details) => {
                      field.onChange(details.checked);
                    }}
                  >
                    필수항목
                  </Checkbox>
                )}
              />
            </VStack>
          </DialogBody>

          <DialogFooter gap={3}>
            <ButtonGroup>
              <DialogActionTrigger asChild>
                <Button
                  variant="subtle"
                  colorPalette="gray"
                  disabled={isSubmitting}
                >
                  닫기
                </Button>
              </DialogActionTrigger>
              <Button
                variant="solid"
                type="submit"
                loading={isSubmitting}
                disabled={!isValid}
              >
                저장
              </Button>
            </ButtonGroup>
          </DialogFooter>

          <DialogCloseTrigger />
        </form>
      </DialogContent>
    </DialogRoot>
  );
};

export default AddTableField;
